import React, { useMemo } from 'react';
import { ResumePreviewProps, WorkExperience, Education, Certification, Project } from '../types';
import { usePreviewSettings } from '../templateSettings';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string, yearOnly?: boolean): string => {
  if (!dateString) return '';
  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";
  if (yearOnly) return date.getFullYear().toString();
  if (!dateFormat) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  }
  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);
  switch (dateFormat) {
    case '%b %y': return `${monthShort} ${yearShort}`;
    case '%B %Y': return `${monthLong} ${year}`;
    case '%b %Y': return `${monthShort} ${year}`;
    case '%m/%Y': return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y': return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y': return year.toString();
    default: return `${monthShort} '${yearShort}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string, yearOnly?: boolean, hideDate?: boolean): string => {
  if (hideDate) return '';
  if (!startDate) return '';
  const start = formatDateWithFormat(startDate, dateFormat, yearOnly);
  if (isCurrent) return `${start} - Present`;
  if (!endDate) return start;
  const end = formatDateWithFormat(endDate, dateFormat, yearOnly);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

export const HemlockTemplate: React.FC<ResumePreviewProps> = ({ data, activeField, onFieldClick, sectionOrder, structures = [], settings }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    categorySkills,
    links,
    customSections
  } = data;

  const dateFormat = settings?.date_format;
  const { rootStyle } = usePreviewSettings(settings);

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
    'TYPE_LINK_BLOCKS': 'links',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) map[sectionId] = s.structure_name;
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  return (
    <div className="resume-preview resume-hemlock" style={rootStyle}>
      <div className="resume-hemlock__header">
        <div className="resume-hemlock__header-left">
          <h1 className="resume-hemlock__name">
            <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick}>
              {personalInfo.firstName}
            </EditableField>
            {' '}
            <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick}>
              {personalInfo.lastName}
            </EditableField>
          </h1>
          {targetJobTitle && (
            <p className="resume-hemlock__job-title">
              <EditableField fieldName="position" activeField={activeField} onFieldClick={onFieldClick}>
                {targetJobTitle}
              </EditableField>
            </p>
          )}
        </div>
        <div className="resume-hemlock__header-right">
          {personalInfo.email && (
            <p>
              <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.email}
              </EditableField>
            </p>
          )}
          {links?.find(l => l.type === 'linkedin')?.url && (
            <p>
              <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                {links.find(l => l.type === 'linkedin')?.url}
              </EditableField>
            </p>
          )}
          {personalInfo.location && (
            <p>
              <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.location}
              </EditableField>
            </p>
          )}
          {personalInfo.phone && (
            <p>
              <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                {personalInfo.phone}
              </EditableField>
            </p>
          )}
        </div>
      </div>

      {professionalSummary && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('about', 'Professional Summary')}</span>
          </h2>
          <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick}>
            <ul className="resume-hemlock__list">
              {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
              ))}
            </ul>
          </EditableBlock>
        </div>
      )}

      {workExperiences && workExperiences.length > 0 && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('experience', 'Experience')}</span>
          </h2>
          {workExperiences.map((exp: WorkExperience) => (
            <EditableBlock key={exp.uid} fieldName={`experience.${exp.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry">
              <div className="resume-hemlock__entry-header">
                <div className="resume-hemlock__entry-title">
                  <EditableField fieldName={`experience.${exp.uid}.job_title`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-role">
                    {exp.job_title}
                  </EditableField>
                  <span className="resume-hemlock__entry-separator"> · </span>
                  <EditableField fieldName={`experience.${exp.uid}.employer_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-company">
                    {exp.employer_name}
                  </EditableField>
                </div>
                <EditableField fieldName={`experience.${exp.uid}.dates`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-date">
                  {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                </EditableField>
              </div>
              {exp.description && (
                <p className="resume-hemlock__entry-description">
                  <EditableField fieldName={`experience.${exp.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                    {exp.description}
                  </EditableField>
                </p>
              )}
              {exp.achievements && exp.achievements.length > 0 && (
                <ul className="resume-hemlock__list">
                  {exp.achievements.map((achievement, idx) => (
                    <li key={idx}>
                      <EditableField fieldName={`experience.${exp.uid}.achievements.${idx}`} activeField={activeField} onFieldClick={onFieldClick}>
                        {achievement}
                      </EditableField>
                    </li>
                  ))}
                </ul>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {volunteerExperiences && volunteerExperiences.length > 0 && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('volunteer', 'Volunteer Experience')}</span>
          </h2>
          {volunteerExperiences.map((exp: WorkExperience) => (
            <EditableBlock key={exp.uid} fieldName={`volunteer.${exp.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry">
              <div className="resume-hemlock__entry-header">
                <div className="resume-hemlock__entry-title">
                  <EditableField fieldName={`volunteer.${exp.uid}.job_title`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-role">
                    {exp.job_title}
                  </EditableField>
                  <span className="resume-hemlock__entry-separator"> · </span>
                  <EditableField fieldName={`volunteer.${exp.uid}.employer_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-company">
                    {exp.employer_name}
                  </EditableField>
                </div>
                <EditableField fieldName={`volunteer.${exp.uid}.dates`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-date">
                  {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                </EditableField>
              </div>
              {exp.achievements && exp.achievements.length > 0 && (
                <ul className="resume-hemlock__list">
                  {exp.achievements.map((achievement, idx) => (
                    <li key={idx}>
                      <EditableField fieldName={`volunteer.${exp.uid}.achievements.${idx}`} activeField={activeField} onFieldClick={onFieldClick}>
                        {achievement}
                      </EditableField>
                    </li>
                  ))}
                </ul>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {educations && educations.length > 0 && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('education', 'Education')}</span>
          </h2>
          {educations.map((edu: Education) => (
            <EditableBlock key={edu.uid} fieldName={`education.${edu.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry">
              <div className="resume-hemlock__entry-header">
                <div className="resume-hemlock__entry-title">
                  <EditableField fieldName={`education.${edu.uid}.institution`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-role">
                    {edu.institution}
                  </EditableField>
                  {edu.degree && (
                    <>
                      <span className="resume-hemlock__entry-separator"> | </span>
                      <EditableField fieldName={`education.${edu.uid}.degree`} activeField={activeField} onFieldClick={onFieldClick}>
                        {edu.degree}
                      </EditableField>
                    </>
                  )}
                </div>
                {edu.graduation_date && (
                  <EditableField fieldName={`education.${edu.uid}.graduation_date`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-date">
                    Grad {formatDateWithFormat(edu.graduation_date, dateFormat)}
                  </EditableField>
                )}
              </div>
              {edu.field_of_study && (
                <p className="resume-hemlock__entry-subtitle">
                  <EditableField fieldName={`education.${edu.uid}.field_of_study`} activeField={activeField} onFieldClick={onFieldClick}>
                    {edu.field_of_study}
                  </EditableField>
                </p>
              )}
              {edu.grade && (
                <p className="resume-hemlock__entry-subtitle">
                  <EditableField fieldName={`education.${edu.uid}.grade`} activeField={activeField} onFieldClick={onFieldClick}>
                    GPA: {edu.grade}
                  </EditableField>
                </p>
              )}
              {edu.description && (
                <p className="resume-hemlock__entry-subtitle">
                  <EditableField fieldName={`education.${edu.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                    {edu.description}
                  </EditableField>
                </p>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {certifications && certifications.length > 0 && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('certifications', 'Certifications')}</span>
          </h2>
          {certifications.map((cert: Certification) => (
            <EditableBlock key={cert.uid} fieldName={`certifications.${cert.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry">
              <div className="resume-hemlock__entry-header">
                <div className="resume-hemlock__entry-title">
                  <EditableField fieldName={`certifications.${cert.uid}.certification_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-role">
                    {cert.certification_name}
                  </EditableField>
                  {cert.organization_name && (
                    <>
                      <span className="resume-hemlock__entry-separator"> | </span>
                      <EditableField fieldName={`certifications.${cert.uid}.organization_name`} activeField={activeField} onFieldClick={onFieldClick}>
                        {cert.organization_name}
                      </EditableField>
                    </>
                  )}
                </div>
                {cert.issued_at && (
                  <EditableField fieldName={`certifications.${cert.uid}.issued_at`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-date">
                    Issued: {formatDateWithFormat(cert.issued_at, dateFormat)}
                  </EditableField>
                )}
              </div>
              {(cert.expires_at || cert.credential_id) && (
                <p className="resume-hemlock__entry-subtitle">
                  {[
                    cert.expires_at && `Expires: ${formatDateWithFormat(cert.expires_at, dateFormat)}`,
                    cert.credential_id && `ID: ${cert.credential_id}`,
                  ].filter(Boolean).join(' · ')}
                </p>
              )}
              {cert.description && (
                <p className="resume-hemlock__entry-subtitle">
                  <EditableField fieldName={`certifications.${cert.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                    {cert.description}
                  </EditableField>
                </p>
              )}
            </EditableBlock>
          ))}
        </div>
      )}

      {links && links.filter(l => l.type !== 'linkedin').length > 0 && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('links', 'Links')}</span>
          </h2>
          <div className="resume-hemlock__links">
            {links.filter(l => l.type !== 'linkedin').map((link, idx) => (
              <EditableBlock key={idx} fieldName={`links.${idx}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__link-item">
                <EditableField fieldName={`links.${idx}.type`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__link-type">
                  {link.type}
                </EditableField>
                <span className="resume-hemlock__link-sep">: </span>
                <EditableField fieldName={`links.${idx}.url`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__link-url">
                  {link.url}
                </EditableField>
              </EditableBlock>
            ))}
          </div>
        </div>
      )}

      {categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0) && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('skills', 'Skills')}</span>
          </h2>
          <div className="resume-hemlock__skills">
            {Object.entries(categorySkills).map(([category, skills]) => (
              skills && skills.length > 0 && (
                <p key={category} className="resume-hemlock__skill-line">
                  <EditableField fieldName={`skills.category.${category}`} activeField={activeField} onFieldClick={onFieldClick}>
                    {category !== 'General skills' && <strong>{category}: </strong>}
                    {skills.join(' · ')}
                  </EditableField>
                </p>
              )
            ))}
          </div>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{getSectionTitle('projects', 'Projects')}</span>
          </h2>
          {projects.map((project: Project) => {
            const dateRange = project.start_date
              ? (project.is_current
                  ? `${formatDateWithFormat(project.start_date, dateFormat)} - Present`
                  : project.end_date
                    ? `${formatDateWithFormat(project.start_date, dateFormat)} - ${formatDateWithFormat(project.end_date, dateFormat)}`
                    : formatDateWithFormat(project.start_date, dateFormat))
              : '';
            return (
              <EditableBlock key={project.uid} fieldName={`projects.${project.uid}`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry">
                <div className="resume-hemlock__entry-header">
                  <div className="resume-hemlock__entry-title">
                    <EditableField fieldName={`projects.${project.uid}.project_name`} activeField={activeField} onFieldClick={onFieldClick} className="resume-hemlock__entry-role">
                      {project.project_name}
                    </EditableField>
                    {project.url && (
                      <>
                        <span className="resume-hemlock__entry-separator"> · </span>
                        <EditableField fieldName={`projects.${project.uid}.url`} activeField={activeField} onFieldClick={onFieldClick}>
                          {project.url}
                        </EditableField>
                      </>
                    )}
                  </div>
                  {dateRange && (
                    <span className="resume-hemlock__entry-date">{dateRange}</span>
                  )}
                </div>
                {project.description && (
                  <p className="resume-hemlock__entry-subtitle">
                    <EditableField fieldName={`projects.${project.uid}.description`} activeField={activeField} onFieldClick={onFieldClick}>
                      {project.description}
                    </EditableField>
                  </p>
                )}
                {project.achievements && project.achievements.length > 0 && (
                  <ul className="resume-hemlock__list">
                    {project.achievements.map((achievement, idx) => (
                      <li key={idx}>{achievement}</li>
                    ))}
                  </ul>
                )}
              </EditableBlock>
            );
          })}
        </div>
      )}

      {customSections?.map((cs) => (
        <div key={cs.id} className="resume-hemlock__section">
          <h2 className="resume-hemlock__section-title">
            <span>{cs.title}</span>
          </h2>
          {cs.layout === 'stacked' ? (
            cs.entries.map((entry) => (
              <div key={entry.id} className="resume-hemlock__entry">
                <div className="resume-hemlock__entry-header">
                  <div className="resume-hemlock__entry-title">
                    <strong className="resume-hemlock__entry-role">{entry.title}</strong>
                  </div>
                  {entry.date && !entry.hideDate && (
                    <span className="resume-hemlock__entry-date">
                      {entry.yearOnly
                        ? new Date(entry.date).getFullYear()
                        : formatDateWithFormat(entry.date, dateFormat)}
                      {entry.endDate && !entry.hideEndDate && (
                        <> - {entry.yearOnly
                          ? new Date(entry.endDate).getFullYear()
                          : formatDateWithFormat(entry.endDate, dateFormat)}</>
                      )}
                    </span>
                  )}
                </div>
                {entry.description && (
                  <p className="resume-hemlock__entry-subtitle">{entry.description}</p>
                )}
              </div>
            ))
          ) : (
            <ul className="resume-hemlock__list">
              {cs.entries.map((entry) => (
                <li key={entry.id}>
                  <strong>{entry.title}</strong>
                  {entry.description && `: ${entry.description}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};
